import { useEffect, useState } from 'react';
import { api, type PatientSummary } from '../api';
import { useI18n } from '../i18n';

/**
 * Choosing a patient in a form: type to search the whole clinic, then pick.
 *
 * The forms used to list the first 100 patients only, so in a clinic with
 * more, the rest could not be chosen at all. The search asks the server (name
 * in either script, number or phone); the patient already chosen stays in the
 * list whatever the search shows.
 */
export function PatientPicker({
  value,
  onChange,
  allowNone = false,
}: {
  value: string;
  onChange: (id: string) => void;
  /** A first "—" option (a booking may be made without a record). */
  allowNone?: boolean;
}) {
  const { t, personName } = useI18n();
  const [q, setQ] = useState('');
  const [found, setFound] = useState<PatientSummary[]>([]);
  const [chosen, setChosen] = useState<PatientSummary | null>(null);

  useEffect(() => {
    let live = true;
    const id = window.setTimeout(
      () => {
        void api.patients({ q: q.trim() || undefined, limit: 50 }).then((r) => {
          if (live) setFound(r.patients);
        });
      },
      q ? 250 : 0,
    );
    return () => {
      live = false;
      window.clearTimeout(id);
    };
  }, [q]);

  // Keep the chosen patient on hand even when the search no longer shows them.
  useEffect(() => {
    if (!value) return setChosen(null);
    const inList = found.find((p) => p.id === value);
    if (inList) return setChosen(inList);
    if (chosen?.id === value) return;
    let live = true;
    void api.patient(value).then((r) => {
      if (live) setChosen(r.patient as unknown as PatientSummary);
    });
    return () => {
      live = false;
    };
  }, [value, found, chosen?.id]);

  const options = chosen && !found.some((p) => p.id === chosen.id) ? [chosen, ...found] : found;

  return (
    <div className="patient-picker">
      <input type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t.patients.search} aria-label={t.patients.search} />
      <select value={value} onChange={(e) => onChange(e.target.value)}>
        {allowNone || !value ? <option value="">—</option> : null}
        {options.map((p) => (
          <option key={p.id} value={p.id}>
            {p.patientNumber} · {personName(p)}
          </option>
        ))}
      </select>
    </div>
  );
}
